/**
 * H2HCardStatsCard - Head-to-head card statistics
 *
 * Uses the same styling pattern as H2HTab for consistency.
 */
import { useState, useMemo } from 'react'
import type { H2HCardStats, TeamLite, CardWindowKey } from '../types'
import { Sparkline } from './Sparkline'

interface H2HCardStatsCardProps {
  h2h: H2HCardStats | null
  homeTeam: TeamLite
  awayTeam: TeamLite
  window: CardWindowKey
}

const WINDOW_LIMITS: Record<CardWindowKey, number> = {
  season: 999,
  last10: 10,
  last5: 5,
  last3: 3,
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  })
}

export function H2HCardStatsCard({
  h2h,
  homeTeam,
  awayTeam,
  window,
}: H2HCardStatsCardProps) {
  const [expanded, setExpanded] = useState(false)

  // Filter matches based on window and compute stats
  const { filteredMatches, avgTotalCards, recentCards, teamStats } = useMemo(() => {
    if (!h2h) return { filteredMatches: [], avgTotalCards: 0, recentCards: [], teamStats: null }

    const limit = WINDOW_LIMITS[window]
    const matches = h2h.match_history.slice(0, limit)

    if (matches.length === 0) {
      return { filteredMatches: [], avgTotalCards: 0, recentCards: [], teamStats: null }
    }

    let totalCards = 0

    // Track stats per team (homeTeam and awayTeam from the fixture)
    // In H2H matches, either team could be home or away
    let team1Yellows = 0
    let team1Reds = 0
    let team1Fouls = 0
    let team1FoulsDrawn = 0
    let team2Yellows = 0
    let team2Reds = 0
    let team2Fouls = 0
    let team2FoulsDrawn = 0

    matches.forEach(m => {
      const hCards = (m.home_yellows ?? 0) + (m.home_reds ?? 0)
      const aCards = (m.away_yellows ?? 0) + (m.away_reds ?? 0)
      totalCards += hCards + aCards

      // Determine which team is which in this match
      // homeTeam.name is the fixture's home team
      const isTeam1Home = m.home_team_name === homeTeam.name
      if (isTeam1Home) {
        team1Yellows += m.home_yellows ?? 0
        team1Reds += m.home_reds ?? 0
        team1Fouls += m.home_fouls ?? 0
        team1FoulsDrawn += m.away_fouls ?? 0
        team2Yellows += m.away_yellows ?? 0
        team2Reds += m.away_reds ?? 0
        team2Fouls += m.away_fouls ?? 0
        team2FoulsDrawn += m.home_fouls ?? 0
      } else {
        team1Yellows += m.away_yellows ?? 0
        team1Reds += m.away_reds ?? 0
        team1Fouls += m.away_fouls ?? 0
        team1FoulsDrawn += m.home_fouls ?? 0
        team2Yellows += m.home_yellows ?? 0
        team2Reds += m.home_reds ?? 0
        team2Fouls += m.home_fouls ?? 0
        team2FoulsDrawn += m.away_fouls ?? 0
      }
    })

    const count = matches.length
    return {
      filteredMatches: matches,
      avgTotalCards: totalCards / count,
      recentCards: matches.slice(0, 5).map(m => m.total_cards).reverse(),
      teamStats: {
        team1: {
          avgYellows: team1Yellows / count,
          avgReds: team1Reds / count,
          avgFouls: team1Fouls / count,
          avgFoulsDrawn: team1FoulsDrawn / count,
        },
        team2: {
          avgYellows: team2Yellows / count,
          avgReds: team2Reds / count,
          avgFouls: team2Fouls / count,
          avgFoulsDrawn: team2FoulsDrawn / count,
        },
      },
    }
  }, [h2h, window, homeTeam.name])

  if (!h2h || h2h.matches === 0) {
    return (
      <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
        <div className="card-inner">
          <div className="small" style={{ textAlign: 'center', color: 'var(--muted)', padding: '20px 0' }}>
            No head-to-head card data available
          </div>
        </div>
      </div>
    )
  }

  const windowLabel = window === 'season' ? '' : ` (${window.replace('last', 'L')})`

  return (
    <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
      <div className="card-inner">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <strong>H2H Card History</strong>
          <span className="small">{filteredMatches.length} meetings{windowLabel}</span>
        </div>

        {/* Summary row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'rgba(255,255,255,.03)', borderRadius: 8, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>{avgTotalCards.toFixed(1)}</div>
            <div className="small">avg cards per match</div>
          </div>
          {recentCards.length > 0 && (
            <Sparkline
              values={recentCards}
              height={28}
              width={80}
              color="var(--yellow)"
            />
          )}
        </div>

        {/* Team header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <span className="badge">
              {homeTeam.crest_url ? <img src={homeTeam.crest_url} alt="" /> : 'H'}
            </span>
            <strong style={{ fontSize: 13 }}>{homeTeam.name}</strong>
          </div>
          <div style={{ color: 'var(--muted)', fontWeight: 600 }}>vs</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
            <strong style={{ fontSize: 13 }}>{awayTeam.name}</strong>
            <span className="badge">
              {awayTeam.crest_url ? <img src={awayTeam.crest_url} alt="" /> : 'A'}
            </span>
          </div>
        </div>

        {/* Stats comparison table - same 4 metrics as season stats */}
        {teamStats && (
          <div className="live-stats">
            <div className="stat-row">
              <span className="stat-value" style={{ textAlign: 'left' }}>{teamStats.team1.avgYellows.toFixed(1)}</span>
              <span className="stat-label">Yellow Cards / Game</span>
              <span className="stat-value" style={{ textAlign: 'right' }}>{teamStats.team2.avgYellows.toFixed(1)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-value" style={{ textAlign: 'left' }}>{teamStats.team1.avgReds.toFixed(2)}</span>
              <span className="stat-label">Red Cards / Game</span>
              <span className="stat-value" style={{ textAlign: 'right' }}>{teamStats.team2.avgReds.toFixed(2)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-value" style={{ textAlign: 'left' }}>{teamStats.team1.avgFouls.toFixed(1)}</span>
              <span className="stat-label">Fouls Committed / Game</span>
              <span className="stat-value" style={{ textAlign: 'right' }}>{teamStats.team2.avgFouls.toFixed(1)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-value" style={{ textAlign: 'left' }}>{teamStats.team1.avgFoulsDrawn.toFixed(1)}</span>
              <span className="stat-label">Fouls Drawn / Game</span>
              <span className="stat-value" style={{ textAlign: 'right' }}>{teamStats.team2.avgFoulsDrawn.toFixed(1)}</span>
            </div>
          </div>
        )}

        {/* Match history toggle - matches H2HTab style */}
        {filteredMatches.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div
              onClick={() => setExpanded(!expanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '8px 0',
                color: 'var(--muted)',
                fontSize: 12,
              }}
            >
              <span>{expanded ? 'Hide' : 'Show'} match history</span>
              <span style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none' }}>
                ▼
              </span>
            </div>

            {expanded && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {filteredMatches.map((match, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 10px',
                      background: 'rgba(255,255,255,.02)',
                      borderRadius: 6,
                      gap: 10,
                    }}
                  >
                    <span style={{ color: 'var(--muted)', fontSize: 11, minWidth: 70 }}>
                      {formatDate(match.date)}
                    </span>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, overflow: 'hidden' }}>
                      <span style={{ fontSize: 12, textAlign: 'right', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {match.home_team_name}
                      </span>
                      <span style={{ color: 'var(--muted)', fontSize: 11 }}>vs</span>
                      <span style={{ fontSize: 12, textAlign: 'left', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {match.away_team_name}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ width: 8, height: 12, background: 'var(--yellow)', borderRadius: 2 }} />
                        <span style={{ fontSize: 11 }}>{(match.home_yellows ?? 0) + (match.away_yellows ?? 0)}</span>
                      </span>
                      {((match.home_reds ?? 0) + (match.away_reds ?? 0)) > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <span style={{ width: 8, height: 12, background: 'var(--red)', borderRadius: 2 }} />
                          <span style={{ fontSize: 11 }}>{(match.home_reds ?? 0) + (match.away_reds ?? 0)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
